import React, { useState } from 'react';
import { status_loading, tweet_notfound, tweet_title } from '@paraglide/messages';
import { useNavigate } from '../../../../src/hooks/useI18nNavigate';
import { useParams } from "react-router";
import { SocialPost } from '@edusense/features-social-post';
import { PageHeader } from '@edusense/shared-ui-layout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  feedGraphQLClient,
  GetFeedItemDocument,
  GetRepliesDocument,
  ToggleLikeDocument,
  AddCommentDocument,
  SharePostDocument
} from '@edusense/feed-client';
import type {
  GetFeedItemQuery,
  GetFeedItemQueryVariables,
  GetRepliesQuery,
  GetRepliesQueryVariables,
  ToggleLikeMutationVariables,
  AddCommentMutationVariables,
  SharePostMutationVariables
} from '@edusense/feed-client';

export interface PostPageProps {
  /**
   * 뒤로가기 핸들러
   */
  onBackClick?: () => void;
}

/**
 * 트윗 상세 페이지 컴포넌트
 *
 * 단일 트윗과 그에 대한 답글들을 표시하는 페이지입니다.
 * feed-client GraphQL을 사용하여 데이터를 로드합니다.
 */
const PostPage: React.FC<PostPageProps> = (props) => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replyText, setReplyText] = useState('');

  // 피드 아이템 조회
  const { data, isLoading, error } = useQuery<GetFeedItemQuery>({
    queryKey: ['GetFeedItem', postId],
    queryFn: async () => {
      const variables: GetFeedItemQueryVariables = { id: postId || '' };
      return feedGraphQLClient.request(GetFeedItemDocument, variables);
    },
    enabled: !!postId
  });

  // 답글 목록 조회
  const { data: repliesData } = useQuery<GetRepliesQuery>({
    queryKey: ['GetReplies', postId],
    queryFn: async () => {
      const variables: GetRepliesQueryVariables = { parentId: postId || '', first: 50 };
      return feedGraphQLClient.request(GetRepliesDocument, variables);
    },
    enabled: !!postId
  });

  // 좋아요 mutation
  const likeMutation = useMutation({
    mutationFn: async (variables: ToggleLikeMutationVariables) => {
      return feedGraphQLClient.request(ToggleLikeDocument, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['GetFeedItem', postId] });
    }
  });

  // 답글 작성 mutation
  const commentMutation = useMutation({
    mutationFn: async (variables: AddCommentMutationVariables) => {
      return feedGraphQLClient.request(AddCommentDocument, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['GetReplies', postId] });
      queryClient.invalidateQueries({ queryKey: ['GetFeedItem', postId] });
      setReplyText('');
    }
  });

  // 리트윗 mutation
  const retweetMutation = useMutation({
    mutationFn: async (variables: SharePostMutationVariables) => {
      return feedGraphQLClient.request(SharePostDocument, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['GetFeedItem', postId] });
    }
  });

  const feedItem = data?.feedItem;
  const replies = repliesData?.replies?.edges?.map(edge => edge.node) || [];

  // 뒤로가기 핸들러
  const handleBackClick = () => {
    if (props.onBackClick) {
      props.onBackClick();
    } else {
      navigate(-1);
    }
  };

  // 좋아요 핸들러
  const handleLike = (id: string) => {
    if (!feedItem) return;
    likeMutation.mutate({
      input: {
        itemId: id,
        isLike: !feedItem.viewerHasLiked
      }
    });
  };

  // 리트윗 핸들러
  const handleRetweet = (id: string) => {
    retweetMutation.mutate({
      input: {
        itemId: id
      }
    });
  };

  // 답글 작성 핸들러
  const handleSubmitReply = () => {
    if (!postId || !replyText.trim()) return;

    commentMutation.mutate({
      input: {
        itemId: postId,
        content: replyText
      }
    });
  };

  if (isLoading) {
    return <div className="p-4 text-center">{status_loading()}</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">에러가 발생했습니다.</div>;
  }

  if (!feedItem) {
    return <div className="p-4 text-center">{tweet_notfound()}</div>;
  }

  // mediaUrls를 이미지와 비디오로 분류
  const images = feedItem.mediaUrls?.filter(url =>
    /\.(jpg|jpeg|png|gif|webp)$/i.test(url)
  ) || [];

  const videos = feedItem.mediaUrls?.filter(url =>
    /\.(mp4|webm|ogg)$/i.test(url)
  ) || [];

  // FeedItem을 SocialPost props로 변환
  const socialPostProps = {
    id: feedItem.id,
    content: feedItem.content,
    author: {
      id: feedItem.author?.id || '',
      name: feedItem.author?.displayName || '',
      username: feedItem.author?.username || '',
      profileImage: feedItem.author?.avatarUrl || '',
      verified: feedItem.author?.isVerified || false
    },
    createdAt: new Date(feedItem.createdAt),
    images,
    videos,
    likesCount: feedItem.engagement?.likes || 0,
    repostsCount: feedItem.engagement?.shares || 0,
    repliesCount: feedItem.engagement?.comments || 0,
    viewsCount: feedItem.engagement?.views || 0,
    isLiked: feedItem.viewerHasLiked,
    isReposted: feedItem.viewerHasShared,
  };

  return (
    <div className="pb-16 md:pb-0">
      {/* 페이지 헤더 */}
      <PageHeader
        title={tweet_title()}
        showBackButton={true}
        onBackClick={handleBackClick}
        useScrollAnimation={true}
      />

      {/* 트윗 상세 */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <SocialPost
          {...socialPostProps}
          showActions={true}
          isDetailView={true}
          onLike={handleLike}
          onRepost={handleRetweet}
        />
      </div>

      {/* 답글 작성 */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800">
        <textarea
          className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="답글을 작성하세요..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          rows={3}
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={handleSubmitReply}
            disabled={!replyText.trim() || commentMutation.isPending}
            className="px-4 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {commentMutation.isPending ? '전송 중...' : '답글 작성'}
          </button>
        </div>
      </div>

      {/* 답글 목록 */}
      <div>
        {replies.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            아직 답글이 없습니다
          </div>
        ) : (
          replies.map((reply) => {
            const replyProps = {
              id: reply.id,
              content: reply.content,
              author: {
                id: reply.author?.id || '',
                name: reply.author?.displayName || '',
                username: reply.author?.username || '',
                profileImage: reply.author?.avatarUrl || '',
                verified: reply.author?.isVerified || false
              },
              createdAt: new Date(reply.createdAt),
              images: reply.mediaUrls?.filter(url => /\.(jpg|jpeg|png|gif|webp)$/i.test(url)) || [],
              videos: reply.mediaUrls?.filter(url => /\.(mp4|webm|ogg)$/i.test(url)) || [],
              likesCount: reply.engagement?.likes || 0,
              repostsCount: reply.engagement?.shares || 0,
              repliesCount: reply.engagement?.comments || 0,
              viewsCount: reply.engagement?.views || 0,
              isLiked: reply.viewerHasLiked,
              isReposted: reply.viewerHasShared,
            };

            return (
              <div key={reply.id} className="border-b border-gray-200 dark:border-gray-800">
                <SocialPost
                  {...replyProps}
                  showActions={true}
                  onLike={handleLike}
                  onRepost={handleRetweet}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PostPage; 